import { useContext, useState } from 'react'
import { AccessTimeOutlined, ContentCopyOutlined, EmailOutlined, PhoneEnabledOutlined, StayPrimaryPortraitOutlined } from '@mui/icons-material'
import { Avatar, Box, Button, Grid, IconButton, Link, Skeleton, Tooltip, Typography } from '@mui/material'

import UserModal from '../../../components/user-modal'
import { AppContext } from '../../../providers/app-provider'
import { useAuthentication } from '../../../providers/auth-provider'
import { getFormattedDate, getLocalFormattedDate, isValidTimezone } from '../../../utils/helpers'
import useClipboard from '../../../utils/hooks/useClipboard'
import useSharedStyles from '../../../utils/shared.styles'
import PeopleContext from '../context'
import useStyles from '../index.styles'

import ClickToContact from './click-to-contact'

const InformationSideBar = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const { user, loading, error, setUser } = useContext(PeopleContext)
  const { roles } = useContext(AppContext)
  const { hasInternalUserAccess } = useAuthentication()

  const { clickToCopy } = useClipboard()

  const [showEditModal, setShowEditModal] = useState(false)
  const [showCopyId, setShowCopyId] = useState<boolean>(false)

  const initials = `${user?.information?.first ? user.information?.first.substring(0, 1) : ''}${
    user?.information?.last ? user.information.last.substring(0, 1) : ''
  }`

  const role = roles?.find((r) => r.id === user?.roleId)?.name

  return (
    <Grid
      container={true}
      sx={[
        sharedClasses.centerContentVertical,
        {
          flexDirection: 'column',
          alignSelf: 'start',
          gap: 1,
        },
        classes.informationSideBar,
      ]}
    >
      {!error && (
        <>
          <Avatar sx={classes.avatar}>{initials && <Typography variant="h4">{initials}</Typography>}</Avatar>
          {loading ? (
            <Skeleton width={220} height={32} />
          ) : (
            <Typography variant="h5">
              {user?.information?.first} {user?.information?.last}
            </Typography>
          )}

          {loading ? (
            <Skeleton width={220} />
          ) : (
            <>
              <Tooltip title={user?.email}>
                <Typography variant="body1" className="fs-exclude" sx={{ ...classes.truncatedText, ...classes.emailSubheader }}>
                  {user?.email}
                </Typography>
              </Tooltip>

              <Grid
                container={true}
                onMouseEnter={() => setShowCopyId(true)}
                onMouseLeave={() => setShowCopyId(false)}
                sx={[sharedClasses.centerContentHorizontal, classes.userIdContainer]}
              >
                <Typography variant="body2" sx={sharedClasses.secondaryText}>
                  #{user?.id}
                  {showCopyId && (
                    <Tooltip title="Copy" disableInteractive={true}>
                      <IconButton
                        size="small"
                        sx={classes.copyIdIconButton}
                        onClick={() => clickToCopy(String(user!.id))}
                        onBlur={() => setShowCopyId(false)}
                        aria-label="Copy user id to clipboard"
                      >
                        <ContentCopyOutlined sx={classes.copyIcon} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
              </Grid>
            </>
          )}

          {loading ? (
            <Skeleton width={120} />
          ) : (
            <Typography variant="body2" sx={sharedClasses.secondaryText}>
              {role}
            </Typography>
          )}

          {hasInternalUserAccess && (
            <Button fullWidth={true} variant="outlined" onClick={() => setShowEditModal(true)}>
              Edit
            </Button>
          )}

          <Box
            sx={[
              {
                alignSelf: 'start',
              },
              classes.contactInfoContainer,
            ]}
          >
            <Typography variant="caption" sx={sharedClasses.secondaryText}>
              Contact info
            </Typography>

            {user?.information?.timezone && isValidTimezone(user.information.timezone) && (
              <Box sx={classes.iconTextBox}>
                <AccessTimeOutlined color="action" />
                <Typography variant="body1">{getLocalFormattedDate(user.information.timezone, 'h:mma')} local time</Typography>
              </Box>
            )}

            <Box sx={classes.iconTextBox}>
              <StayPrimaryPortraitOutlined color="action" />
              {loading ? (
                <Skeleton width={130} />
              ) : (
                <>
                  {user?.phone ? (
                    <ClickToContact text={user.phone} protocol="tel" />
                  ) : (
                    <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setShowEditModal(true)}>
                      + Add Mobile Number
                    </Link>
                  )}
                </>
              )}
            </Box>

            <Box sx={classes.iconTextBox}>
              <PhoneEnabledOutlined color="action" />
              {loading ? (
                <Skeleton width={130} />
              ) : (
                <>
                  {user?.metadata?.backup_phone ? (
                    <ClickToContact text={user.metadata.backup_phone} protocol="tel" />
                  ) : hasInternalUserAccess ? (
                    <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setShowEditModal(true)}>
                      + Add Backup Number
                    </Link>
                  ) : (
                    'N/A'
                  )}
                </>
              )}
            </Box>
            <Box sx={classes.iconTextBox}>
              <EmailOutlined color="action" />
              {loading ? (
                <Skeleton width={220} />
              ) : (
                <>
                  {user?.email ? (
                    <ClickToContact text={user.email} protocol="mailto" />
                  ) : (
                    <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setShowEditModal(true)}>
                      + Add Email Address
                    </Link>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Typography variant="caption" sx={classes.auditText}>
            Added: {user?.createdAt ? getFormattedDate(user.createdAt, 'LLL d, yyyy') : ''}
            <br />
            Last updated: {user?.updatedAt ? getFormattedDate(user.updatedAt, 'LLL d, yyyy') : ''}
          </Typography>
        </>
      )}
      <UserModal open={showEditModal} setOpen={setShowEditModal} mode="edit" user={user} setUser={setUser} />
    </Grid>
  )
}

export default InformationSideBar
